import { useMemo, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined'
import { colors } from '../../const/colors'
import {
  IParkingPoolUser,
  ITenantUsers,
  AccessCardType,
} from '../../graphql/users/queryUsersList'
import RTTable from '../../components/RTTable'
import { ID } from '../../graphql/IQueryData'
import { IParkingPool } from '../../graphql/parkingPool/getTenantParkingPoolUsers'
import {
  NameCell,
  MutedCell,
  ProfileCell,
  ParkingsCell,
  ServiceCell,
  ReadinessCell,
} from '../Table/TableCells'

interface ITableColumns {
  Header: string
  accessor:
    | 'fullName'
    | 'login'
    | 'workEmail'
    | 'tenantName'
    | 'assigned'
    | 'readiness'
    | 'cardType'
    | 'cardNumber'
    | 'carPlate'
    | 'carPlate2'
    | 'profile'
    | 'parkings'
  isVisible?: boolean
  Cell?: (props: { value: any; row: any }) => React.ReactNode
}

export interface IUserDataRow {
  fullName: string
  firstName: string
  lastName: string
  login: string
  workEmail?: string
  tenantName: string
  tenantId: string
  accessCard?: {
    number: string
    type: AccessCardType
  }
  seos?: {
    disabled?: boolean
    id: ID
    prefix: string
  }
  parkingPoolData: {
    id: string
    carPlate?: string
    carPlate2?: string
    enabled?: boolean
  }
  assigned: string
  isAssigned: boolean
  isReady: boolean
  readiness: string
  cardType?: AccessCardType
  cardNumber?: string
  carPlate?: string
  carPlate2?: string
  profile?: string
  parkings?: string
}

const ParkingPoolUsersTable = ({
  children,
  users,
  tenant,
  assigned,
  setOpenUserDetails,
  parkingPool,
  onSelectionChange,
  onReserve,
}: {
  children?: ReactNode
  users: IParkingPoolUser[]
  tenant?: ITenantUsers
  assigned: Set<string>
  setOpenUserDetails: (userData: IUserDataRow | undefined) => void
  parkingPool: IParkingPool
  onSelectionChange?: (rows: IUserDataRow[]) => void
  onReserve?: (row: IUserDataRow) => void
}) => {
  const { t: translate } = useTranslation([
    'columns',
    'user_details',
    'admin_menu',
    'bool',
    'parking_pool_users',
  ])
  const { profiles } = parkingPool

  const yesLabel = translate('bool:yes')
  const noLabel = translate('bool:no')

  const parkingsByProfile = useMemo(
    () =>
      new Map(
        profiles.map(profile => [
          profile.id,
          (profile.parkings ?? [])
            .map(entry => entry.parking?.name)
            .filter(Boolean)
            .join(', '),
        ]),
      ),
    [profiles],
  )

  const tableColumns: ITableColumns[] = useMemo(
    () => [
      {
        Header: translate('full_name'),
        accessor: 'fullName',
        Cell: ({ value }: { value: string }) => <NameCell value={value} />,
      },
      {
        Header: translate('login'),
        accessor: 'login',
        Cell: ({ value }: { value: string }) => <MutedCell value={value} />,
      },
      {
        Header: translate('work_email'),
        accessor: 'workEmail',
        Cell: ({ value }: { value: string }) => <MutedCell value={value} />,
      },
      {
        Header: translate('parkings'),
        accessor: 'parkings',
        Cell: ({ value }: { value: string }) => <ParkingsCell value={value} />,
      },
      {
        Header: translate('profile'),
        accessor: 'profile',
        isVisible: !!profiles.length,
        Cell: ({ value }: { value: string }) => <ProfileCell value={value} />,
      },
      {
        Header: translate('card_and_plate'),
        accessor: 'readiness',
        Cell: ({ value, row }: { value: string; row: any }) => (
          <ReadinessCell value={value} isReady={row.original.isReady} />
        ),
      },
      {
        Header: translate('service'),
        accessor: 'assigned',
        Cell: ({ value }: { value: string }) => (
          <ServiceCell value={value} yesLabel={yesLabel} />
        ),
      },
      {
        Header: translate('card_type'),
        accessor: 'cardType',
        isVisible: false,
      },
      {
        Header: translate('card_number'),
        accessor: 'cardNumber',
        isVisible: false,
      },
      {
        Header: translate('car_plate'),
        accessor: 'carPlate',
        isVisible: false,
      },
      {
        Header: translate('car_plate2'),
        accessor: 'carPlate2',
        isVisible: false,
      },
    ],
    [translate, profiles.length, yesLabel],
  )

  const tableData: IUserDataRow[] = useMemo(
    () =>
      users.map(
        ({
          login,
          firstName,
          lastName,
          email,
          workEmail,
          accessCard,
          parkingPoolData,
          parkingPoolProfile,
        }) => {
          const hasCard = !!accessCard?.number
          const hasPlate = !!(
            parkingPoolData?.carPlate || parkingPoolData?.carPlate2
          )
          return {
            login,
            firstName,
            lastName,
            fullName: `${firstName} ${lastName}`,
            workEmail: workEmail?.email ? workEmail.email : email?.email,
            tenantName: tenant?.name || '',
            tenantId: tenant?.id || '',
            accessCard,
            seos: tenant?.seos,
            parkingPoolData,
            assigned: assigned.has(login) ? yesLabel : noLabel,
            isAssigned: assigned.has(login),
            isReady: hasCard && hasPlate,
            readiness: translate(
              hasCard && hasPlate
                ? 'parking_pool_users:ready_complete'
                : 'parking_pool_users:ready_missing',
            ),
            cardType: accessCard?.type,
            cardNumber: accessCard?.number,
            carPlate: parkingPoolData?.carPlate,
            carPlate2: parkingPoolData?.carPlate2,
            profile: parkingPoolProfile?.name || '-',
            parkings: parkingPoolProfile?.id
              ? (parkingsByProfile.get(parkingPoolProfile.id) ?? '')
              : '',
          }
        },
      ) || [],
    [users, tenant, assigned, yesLabel, noLabel, parkingsByProfile, translate],
  )

  return (
    <RTTable
      columns={tableColumns as any}
      data={tableData}
      onRowClick={rowData => setOpenUserDetails(rowData)}
      onSelectionChange={onSelectionChange}
      actionsComponent={row =>
        onReserve && row.isAssigned ? (
          <Box
            data-e2e="row-reserve"
            onClick={event => {
              event.stopPropagation()
              onReserve(row)
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: 34,
              px: '14px',
              borderRadius: '10px',
              background: colors.greenMuted,
              color: colors.navy,
              fontSize: '12.5px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              transition: 'background .15s',
              '&:hover': { background: '#9BBE9C' },
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 15 }} />
            {translate('user_details:reserve')}
          </Box>
        ) : null
      }
      pagination
      orderByProps="fullName"
      sortDesc={false}
      rowSelect
      isRowSelectable={row => row.isAssigned || row.isReady}
      selectDisabledHint={translate(
        'parking_pool_users:select_requires_service',
      )}
      gFilter={false}
    >
      {children}
    </RTTable>
  )
}

export default ParkingPoolUsersTable
